import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TaskDetailsDialog } from "./TaskDetailsDialog";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Trash2, Eye, CheckCircle, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { isTaskUrgent } from "@/lib/utils";

interface TaskTableViewProps {
  tasks: any[];
  userRole?: string | null;
  onTaskComplete: (taskId: string) => Promise<void>;
  onStatusChange?: (taskId: string, newStatus: string) => Promise<void>;
  showOldCompletedTasks?: boolean;
}

export const TaskTableView = ({ 
  tasks, 
  userRole, 
  onTaskComplete,
  onStatusChange,
  showOldCompletedTasks = false
}: TaskTableViewProps) => {
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  const handleDelete = async () => {
    if (!taskToDelete?.id || isDeleting || !currentOrganization) return;

    try {
      setIsDeleting(true);
      setShowDeleteConfirm(false);

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: "delete",
          taskId: taskToDelete.id,
          organizationId: currentOrganization.id
        }
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setTaskToDelete(null);
    }
  };

  const handleTaskClick = (task: any) => {
    setSelectedTask(task);
    setIsDetailsOpen(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'in_progress': return 'secondary';
      case 'on_hold': return 'outline';
      default: return 'secondary';
    }
  };

  const handleStatusChange = (taskId: string, newStatus: string) => {
    if (onStatusChange) {
      onStatusChange(taskId, newStatus);
    }
  };

  const availableStatuses = [
    { value: "todo", label: "To Do" },
    { value: "in_progress", label: "In Progress" },
    { value: "on_hold", label: "On Hold" },
    { value: "completed", label: "Completed" }
  ];

  return (
    <>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Assigned To</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => {
              const urgent = isTaskUrgent(task);
              return (
              <TableRow 
                key={task.id} 
                className={`cursor-pointer transition-colors ${
                  urgent 
                    ? 'bg-red-50 hover:bg-red-100' 
                    : 'hover:bg-muted/50'
                }`}
              >
                <TableCell 
                  onClick={() => handleTaskClick(task)}
                  className="font-medium max-w-xs"
                >
                  <div>
                    <div className="font-medium truncate">{task.title}</div>
                    {task.description && (
                      <div className="text-sm text-muted-foreground truncate mt-1">
                        {task.description}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {onStatusChange ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-auto p-0 hover:bg-transparent">
                          <Badge variant={getStatusColor(task.status)} className="cursor-pointer hover:opacity-80 flex items-center gap-1">
                            {task.status?.replace('_', ' ') || 'No Status'}
                            <ChevronDown className="h-3 w-3" />
                          </Badge>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {availableStatuses.map((status) => (
                          <DropdownMenuItem
                            key={status.value}
                            onClick={() => handleStatusChange(task.id, status.value)}
                            className={task.status === status.value ? "bg-muted" : ""}
                          >
                            <Badge variant={getStatusColor(status.value)} className="mr-2">
                              {status.label}
                            </Badge>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Badge variant={getStatusColor(task.status)} onClick={() => handleTaskClick(task)} className="cursor-pointer">
                      {task.status?.replace('_', ' ') || 'No Status'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell onClick={() => handleTaskClick(task)}>
                  <Badge variant={getPriorityColor(task.priority)}>
                    {task.priority || 'No Priority'}
                  </Badge>
                </TableCell>
                <TableCell onClick={() => handleTaskClick(task)}>
                  <div className="text-sm">
                    {task.assignee?.full_name || 'Unassigned'}
                  </div>
                </TableCell>
                <TableCell onClick={() => handleTaskClick(task)}>
                  <div className="text-sm">
                    {task.due_date ? format(new Date(task.due_date), 'MMM dd, yyyy') : 'No due date'}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTaskClick(task)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {task.status !== 'completed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onTaskComplete(task.id)}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    {(userRole === 'salesman' || task.created_by === task.current_user_id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTaskToDelete(task);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
      </div>

      <TaskDetailsDialog
        task={selectedTask}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        userRole={userRole}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
      />
    </>
  );
};